import { get } from 'lodash';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsLaminateSelected } from '~/api/easy-estimator-api/materials-api/hooks/useIsLaminateSelected';
import {
  BACKSPLASH_DEFAULT_VALUE,
  WATERFALL_DEFAULT_VALUE,
  getSideItems,
} from '~/core/constants';
import { SideItemType } from '~/core/enums';
import { ArrowInfo, SideItemInfoModel } from '~/core/models';
import { useDrawingShapeContext } from '~/legacy/easy-estimator-page/context';

export const useArrowSideItemInfoModel = (): {
  getArrowWaterfall: (arrow: ArrowInfo) => SideItemInfoModel | undefined;
  getArrowWallSide: (arrow: ArrowInfo) => SideItemInfoModel | undefined;
  getArrowSideItems: (arrow: ArrowInfo) => SideItemInfoModel[] | undefined;
} => {
  const {
    getters: { activeDrawingShape },
  } = useDrawingShapeContext();
  const { t } = useTranslation();
  const isLaminateSelected = useIsLaminateSelected();

  const getArrowSideItems = useCallback(
    (arrow: ArrowInfo) => {
      if (!activeDrawingShape) {
        return undefined;
      }
      const translationBase: Record<SideItemType, string> = {
        [SideItemType.WALL_SIDE]: isLaminateSelected
          ? 'SHAPE_TAB.OPTIONS.RAW'
          : 'SHAPE_TAB.OPTIONS.WALL_SIDE',
        [SideItemType.APPLIANCE_SIDE]: 'SHAPE_TAB.OPTIONS.APPLIANCE_SIDE',
        [SideItemType.WATERFALL]: 'SHAPE_TAB.OPTIONS.WATERFALL',
        [SideItemType.END_CAP]: 'SHAPE_TAB.OPTIONS.END_CAP',
        [SideItemType.END_SPLASH]: 'SHAPE_TAB.OPTIONS.END_SPLASH',
      };

      const removedEdge =
        activeDrawingShape.removedElementsHistory?.edgesAndCorners?.find(
          (ec) => ec.drawingEdge.id === arrow.drawingEdgeId
        );

      const removedDepth = removedEdge?.drawingEdge.sideItem?.depth;

      const isDisabled = (activeDrawingShape.drawingEdges ?? []).some(
        (edge) => !!edge.customSideType && edge.id === arrow.drawingEdgeId
      );

      return getSideItems(isLaminateSelected).map((sideType, index) => {
        const prefix = get(translationBase, sideType, 'UNKNOWN_TYPE');
        const isChecked = sideType === removedEdge?.sideItemType;

        const hasHeight = isChecked && !!removedDepth;
        const fieldsBy: Record<SideItemType, Partial<SideItemInfoModel>> = {
          [SideItemType.WALL_SIDE]: {
            backsplash: {
              checked: hasHeight,
              height: isChecked ? removedDepth : BACKSPLASH_DEFAULT_VALUE,
            },
          },
          [SideItemType.APPLIANCE_SIDE]: {},
          [SideItemType.WATERFALL]: {
            waterfallHeight: hasHeight ? removedDepth : WATERFALL_DEFAULT_VALUE,
          },
          [SideItemType.END_CAP]: {},
          [SideItemType.END_SPLASH]: {},
        };
        const sideItemInfo: SideItemInfoModel = {
          id: index + 1,
          sideType,
          label: t(`${prefix}.LABEL`),
          value: t(`${prefix}.VALUE`),
          drawingEdgeId: arrow.drawingEdgeId as string,
          disabled: isDisabled,
          checked: isChecked,
          ...fieldsBy[sideType],
        };

        return sideItemInfo;
      });
    },
    [activeDrawingShape, t, isLaminateSelected]
  );

  const getArrowWaterfall = useCallback(
    (arrow: ArrowInfo) => {
      const sideItems = getArrowSideItems(arrow);

      return (sideItems ?? []).find(
        (sideItem) => sideItem.sideType === SideItemType.WATERFALL
      );
    },
    [getArrowSideItems]
  );

  const getArrowWallSide = useCallback(
    (arrow: ArrowInfo) => {
      const sideItems = getArrowSideItems(arrow);

      return (sideItems ?? []).find(
        (sideItem) => sideItem.sideType === SideItemType.WALL_SIDE
      );
    },
    [getArrowSideItems]
  );

  return {
    getArrowWaterfall,
    getArrowWallSide,
    getArrowSideItems,
  };
};
